import { createRouter, createWebHistory } from 'vue-router'
import { supabase } from '@/lib/supabase'
import Home from '@/views/Home.vue'
import Data from '@/views/Data.vue'
import Pricing from '@/views/Pricing.vue'
import Influencers from '@/views/Influencers.vue'
import InfluencerApply from '@/views/InfluencerApply.vue'
import Login from '@/views/Login.vue'
import Register from '@/views/Register.vue'
import Dashboard from '@/views/Dashboard.vue'
import ProDashboard from '@/views/ProDashboard.vue'
import LeagueTrends from '@/views/quick-stats/LeagueTrends.vue'
import TeamRankings from '@/views/quick-stats/TeamRankings.vue'
import GamePreviews from '@/views/quick-stats/GamePreviews.vue'
import TeamDetail from '@/views/quick-stats/TeamDetail.vue'
import GameDetail from '@/views/quick-stats/GameDetail.vue'

const influencerEnabled = import.meta.env.VITE_FEATURE_INFLUENCER === 'true'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { title: 'Home' }
  },
  {
    path: '/data',
    name: 'Data',
    component: Data,
    meta: { title: 'Info' }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: {
      title: 'Pricing',
      description: 'Paulie Pro pricing: one monthly tier with iOS app access, real-time alerts, quick stats, database chat, and the Pro Dashboard.'
    }
  },
  {
    path: '/pro-dashboard',
    name: 'ProDashboard',
    component: ProDashboard,
    meta: {
      title: 'Pro Dashboard',
      description: 'Paulie account dashboard for private web tools.'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: 'Login' }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { title: 'Register' }
  },
  {
    path: '/quick-stats',
    redirect: '/quick-stats/game-previews',
    meta: {
      title: 'Data',
      description: 'Public sports betting trend snapshots and game preview lines from Paulie public data.'
    }
  },
  {
    path: '/quick-stats/game-previews',
    name: 'GamePreviews',
    component: GamePreviews,
    meta: {
      title: 'Game Previews',
      description: 'Spread, total, and sportsbook-labeled moneyline game preview lines in a factual public view.'
    }
  },
  {
    path: '/quick-stats/league-trends',
    name: 'LeagueTrends',
    component: LeagueTrends,
    meta: {
      title: 'League Trends',
      description: 'Daily league trend rows grouped by league, market, and timeframe.'
    }
  },
  {
    path: '/quick-stats/team-rankings',
    name: 'TeamRankings',
    component: TeamRankings,
    meta: {
      title: 'Team Rankings',
      description: 'Team trend rankings with links to team-level quick stats pages.'
    }
  },
  {
    path: '/quick-stats/game-previews/:gameSlug',
    name: 'GamePreviewDetail',
    component: GameDetail,
    meta: {
      title: 'Game Preview Detail',
      description: 'Factual game preview details with sportsbook-labeled moneyline values when available.'
    }
  },
  {
    path: '/quick-stats/team-rankings/:teamSlug',
    name: 'TeamQuickStats',
    component: TeamDetail,
    meta: {
      title: 'Team Data',
      description: 'Team-level quick stats and timeframe breakdowns from public data.'
    }
  },
  {
    path: '/quick-stats/teams/:teamSlug',
    redirect: (to) => `/quick-stats/team-rankings/${to.params.teamSlug}`
  },
  ...(influencerEnabled ? [
    {
      path: '/influencers',
      name: 'Influencers',
      component: Influencers,
      meta: { title: 'Influencers' }
    },
    {
      path: '/influencer-apply',
      name: 'InfluencerApply',
      component: InfluencerApply,
      meta: { title: 'Influencer Application' }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: { title: 'Dashboard', requiresAuth: true }
    },
  ] : [])
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
  // Update page title
  document.title = to.meta.title ? `${to.meta.title} - Paulie` : 'Paulie'
  const descriptionValue = to.meta.description || 'Public sports betting data and trend pages from Paulie.'
  const currentDescription = document.querySelector('meta[name=\"description\"]')
  if (currentDescription) {
    currentDescription.setAttribute('content', descriptionValue)
  } else {
    const descriptionMeta = document.createElement('meta')
    descriptionMeta.setAttribute('name', 'description')
    descriptionMeta.setAttribute('content', descriptionValue)
    document.head.appendChild(descriptionMeta)
  }

  const canonicalHref = `${window.location.origin}${to.fullPath}`
  let canonicalLink = document.querySelector('link[rel="canonical"]')
  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalLink)
  }
  canonicalLink.setAttribute('href', canonicalHref)
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Auth error:', error)
        next('/login')
        return
      }
      
      if (!session) {
        next('/login')
        return
      }
      
      next()
    } catch (error) {
      console.error('Auth check error:', error)
      next('/login')
    }
  } else {
    next()
  }
})

export default router 
